import React, { useEffect, useState } from "react";
import { apiClient } from "api";
import {
  DataGridPro,
  GridColDef,
  GridDensity,
  GridRenderCellParams,
  useGridApiRef,
} from "@mui/x-data-grid-pro";
import { LinearProgress, Stack, TablePagination } from "@mui/material";
import { MUI_TABLE_STYLES, TABLE_PAGINATION_STYLES } from "constants/constants";
import { useSelector } from "react-redux";
import { AppState } from "store/types";
import I18nKey from "translations/I18nKey";
import { useIntl } from "react-intl";

interface UserData {
  UserName: string;
  Email: string;
  CompanyId: string;
  CreatedAt: string;
  PaymentStatus: string;
  ActiveSeller: any;
  InActiveSeller: any;
}
const UserAnalytics = () => {
  const [data, setData] = useState<UserData[]>([]);
  const [pageSize, setPageSize] = useState<number>(25);
  const [page, setPage] = useState<number>(0);
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [errors, setErrors] = useState<string>("");
  const apiRef = useGridApiRef();
  const intl = useIntl();

  const density = useSelector(
    (state: AppState) => state.table.density
  ) as GridDensity;

  const columns: GridColDef[] = [
    {
      field: "SellerName",
      headerName: "Seller Name",
      flex: 0.75,
      renderCell: (params: GridRenderCellParams) => {
        return params.value ? params.value : "-";
      },
    },
    {
      field: "Email",
      headerName: "Email",
      cellClassName: "left-align-field",
      flex: 1.5,
      renderCell: (params: GridRenderCellParams) => {
        return params.value ? params.value : "-";
      },
    },
    {
      field: "CompanyId",
      headerName: "Company ID",
      align: "right",
      type: "number",
      flex: 1.25,
      renderCell: (params: GridRenderCellParams) => {
        return params.value ? params.value : "-";
      },
    },
    {
      field: "CreatedAt",
      headerName: "Created At",
      cellClassName: "left-align-field",
      flex: 1.25,
      renderCell: (params: GridRenderCellParams) => {
        return params.value ? params.value : "-";
      },
    },
    {
      field: "PaymentStatus",
      headerName: "Payment Status",
      flex: 0.75,
      renderCell: (params: GridRenderCellParams) => {
        return params.value ? params.value : "-";
      },
    },
  ];

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
    apiRef.current.setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const size = parseInt(event.target.value, 10);
    setPageSize(parseInt(event.target.value, 10));
    setPage(0);
    apiRef.current.setPage(0);
    apiRef.current.setPageSize(size);
  };

  useEffect(() => {
    setIsFetching(true);
    apiClient
      .getUserAnalytics()
      .then((response) => {
        if (response.data) {
          const rows = response.data?.map((item: any, id: number) => {
            return { ...item, id };
          });
          setData(rows);
        } else {
          setErrors(response.errors);
        }
      })
      .catch((error) => {
      })
      .finally(() => setIsFetching(false));
  }, []);

  const CustomFooter = () => {
    return (
      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 50, 100, 200]}
        component="div"
        sx={TABLE_PAGINATION_STYLES}
        count={data.length}
        showLastButton
        showFirstButton
        rowsPerPage={pageSize}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    );
  };

  return (
    <>
      <DataGridPro
        components={{
          Footer: CustomFooter,
          NoRowsOverlay: () => (
            <Stack height="100%" alignItems="center" justifyContent="center">
              {intl.formatMessage({
                id: I18nKey.NO_DATA_MSG,
              })}
            </Stack>
          ),
          LoadingOverlay: LinearProgress,
        }}
        apiRef={apiRef}
        rows={data}
        sx={MUI_TABLE_STYLES}
        columns={columns}
        pageSize={pageSize}
        headerHeight={70}
        loading={isFetching}
        density={density}
        pagination
      />
    </>
  );
};

export default UserAnalytics;
